import {computed, watch, ref} from 'vue';
import {useRoute, useRouter} from "vue-router";
import {useCookie, useState, useNuxtApp} from '#app';
import {useGoogleRecaptcha} from "~/composables/useGoogleRecaptcha";
import {endpoints} from "~/network/endpoints.js";
import {useHttpRequest} from "~/composables/useHttpRequest";
import {useMessagingSignalR} from "~/composables/useMessagingSignalR";
import {useStripeConnect} from "~/composables/useStripeConnect";

import {MFA_DEVICE_HEADER, readDeviceToken} from '~/utils/mfa';
import {sessionSteps} from '~/utils/sessionFreeRoutes';
import {userForCookie, userFromCookie} from '~/utils/userCookie.js';

export function useAuthService() {
    const router = useRouter();
    const route = useRoute();

    const {setAlertMessage} = useAlertService();

    // SignalR instance (only on client)
    const signalR = process.client ? useMessagingSignalR() : null;

    const {executeRecaptcha} = useGoogleRecaptcha();
    // Using useCookie to store tokens securely
    // Session cookies live 7 days from the LAST server-rendered visit, not from
    // sign-in: init() re-issues them on the server (see touchSessionCookies), and
    // `refresh` makes Nuxt send the Set-Cookie header even though the value is
    // unchanged. Server-set matters — Safari and Brave cap script-written
    // cookies at 7 days and purge them after 7 days without a visit, but honour
    // an HTTP Set-Cookie in full.
    const SESSION_COOKIE_MAX_AGE = 60 * 60 * 24 * 7;
    const sessionCookieOptions = {maxAge: SESSION_COOKIE_MAX_AGE, refresh: true};
    const userCookie = useCookie('user', sessionCookieOptions);
    const accessTokenCookie = useCookie('accessToken', sessionCookieOptions);
    const impersonatingUserCookie = useCookie('impersonatingUser', sessionCookieOptions);
    const impersonatingAccessTokenCookie = useCookie('impersonatingAccessToken', sessionCookieOptions);

    // Re-issue every present session cookie with a full max-age. Assigning the
    // same value flags it as changed for Nuxt's server cookie writer, which then
    // emits Set-Cookie at render time; on the client this is a no-op by design.
    const touchSessionCookies = () => {
        if (!import.meta.server) return;
        for (const cookie of [userCookie, accessTokenCookie, impersonatingUserCookie, impersonatingAccessTokenCookie]) {
            if (cookie.value) cookie.value = cookie.value;
        }
    };

    const _user = useState('user', () => userFromCookie(userCookie.value));
    const user = computed(() => _user.value ?? {});
    // Whether this session holds a user READ FROM THE API rather than the copy
    // the cookie started it with. updateLocalUser swallows a failed refresh on
    // purpose (an unreachable API must not log anyone out), so without this flag
    // nothing downstream can tell the two apart — and the required-prompt wall
    // turns on `pendingFormPrompt`, a field only the refresh can add.
    const userRefreshed = useState('userRefreshed', () => false);
    const accessToken = useState('accessToken', () => accessTokenCookie.value || null);
    const impersonatingUser = useState('impersonatingUser', () => userFromCookie(impersonatingUserCookie.value));
    const impersonatingAccessToken = useState('impersonatingAccessToken', () => impersonatingAccessTokenCookie.value || null);

    // Forgot Password Action
    const {
        item: forgotPasswordItem,
        createItem: forgotPasswordAction,
        formPending: forgotPasswordLoading,
        formErrors: forgotPasswordErrors,
        hasFormErrors: hasForgotPasswordErrors
    } = useCrudApi('api/v2/auth/forgot-password');

    // Login Action
    const {
        item: loginItem,
        createItem: loginAction,
        formPending: loginLoading,
        formErrors: loginErrors,
        hasFormErrors: hasLoginErrors
    } = useCrudApi('api/v2/auth/login');

    const {
        item: loginVerificationItem,
        createItem: loginVerification,
        formPending: loginVerificationLoading,
        formErrors: loginVerificationErrors,
        hasFormErrors: hasLoginVerificationErrors
    } = useCrudApi('api/v2/auth/login/verification');

    const {
        item: registerVerificationItem,
        createItem: registerVerification,
        formPending: registerVerificationLoading,
        formErrors: registerVerificationErrors,
        hasFormErrors: hasRegisterVerificationErrors
    } = useCrudApi('api/v2/auth/register/verification');

    const {
        item: externalLoginItem,
        createItem: externalLoginAction,
        formPending: externalLoginLoading,
        formErrors: externalLoginErrors,
        hasFormErrors: hasExternalLoginErrors
    } = useCrudApi('api/v2/auth/external-login');

    // Join with invite link state
    const joinPending = ref(false);
    const joinError = ref(null);

    const {
        item: externalLoginLinkItem,
        createItem: externalLoginLinkAction,
        formPending: externalLoginLinkLoading,
        formErrors: externalLoginLinkErrors,
        hasFormErrors: hasExternalLoginLinkErrors
    } = useCrudApi('api/v2/auth/external-login-link');

    const {
        item: externalLoginDeleteItem,
        deleteItem: externalLoginDeleteAction,
        formPending: externalLoginDeleteLoading,
        formErrors: externalLoginDeleteErrors,
        hasFormErrors: hasExternalLoginDeleteErrors
    } = useCrudApi('api/v2/auth/external-login');

    // Register Action
    const {
        item: registerItem,
        createItem: registerAction,
        formPending: registerLoading,
        formErrors: registerErrors,
        hasFormErrors: hasRegisterErrors
    } = useCrudApi('api/v2/auth/me');
    const {item: meItem, getItem: getMe, itemPending: meLoading, itemErrors: meErrors} = useCrudApi('api/v2/auth/me');
    const {
        item: impersonateItem,
        getItem: impersonate,
        itemPending: impersonateLoading,
        itemErrors: impersonateErrors
    } = useCrudApi('api/v2/users');


    // `session: false` is a page that never uses the sign-in session (sessionFreeRoutes: the
    // anonymous feedback page). The state is still read from the cookies, which makes no request,
    // but nothing else of the session runs there. Every other page passes true.
    const init = async ({session = true} = {}) => {
        _user.value = userFromCookie(userCookie.value);
        accessToken.value = accessTokenCookie.value || null;
        impersonatingUser.value = userFromCookie(impersonatingUserCookie.value);
        impersonatingAccessToken.value = impersonatingAccessTokenCookie.value || null;
        const steps = sessionSteps({session, authenticated: isAuthenticated.value});
        if (steps.authHeaders) updateAuthHeaders();
        if (steps.liveConnection) startLiveConnection();
        if (steps.touchCookies) touchSessionCookies();
        if (steps.refresh) await updateLocalUser();
        if (steps.logoutOn401) {
            setCrudOnUnauthenticated(() => {
                logout();
            });
        }
    };
    const getAccessToken = () => {
        if (impersonatingAccessToken.value) {
            return impersonatingAccessToken.value;
        } else if (accessToken.value) {
            return accessToken.value;
        }
        return null;
    }

    const updateLocalUser = async () => {
        try {
            await getMe("");
        } catch (e) {
            // Unreachable API (restart, network blip) must not destroy the session
        }
        // Only replace the cached user when the refresh actually returned one —
        // assigning null here wipes the user cookie and silently logs out on the
        // next route. A genuine 401 still logs out via setCrudOnUnauthenticated.
        if (meItem.value) {
            _user.value = meItem.value;
            userRefreshed.value = true;
        }
    }

    const updateAuthHeaders = () => {
        // useCookie works on both server and client in Nuxt 3
        // On server: reads from request headers
        // On client: reads from document.cookie
        const timezoneCookie = useCookie('timezone');
        const timezone = timezoneCookie.value;

        // Only include timezone header if we actually have a timezone value
        const baseHeaders = {};
        if (impersonatingAccessToken.value) {
            setCrudHeadersGenerator(() => ({
                ...baseHeaders,
                Authorization: `Bearer ${impersonatingAccessToken.value}`,
            }));
        } else if (accessToken.value) {
            if (timezone) {
                baseHeaders['X-Timezone-Identifier'] = timezone;
            }
            // A browser remembered after a two-step sign-in says so on every
            // call (MFA_PLAN.md); the API ignores it for anyone else's account.
            const device = process.client ? readDeviceToken() : null;
            if (device) baseHeaders[MFA_DEVICE_HEADER] = device;
            setCrudHeadersGenerator(() => ({
                ...baseHeaders,
                Authorization: `Bearer ${accessToken.value}`,
            }));
        } else {
            setCrudHeadersGenerator(() => ({
                ...baseHeaders,
            }));
        }
    };

    const login = async (credentials) => {
        await loginAction(credentials);
        const result = loginItem.value;
        if (result) {
            accessToken.value = result.accessToken;
            _user.value = result;
            // Store in cookies
            accessTokenCookie.value = result.accessToken;
            userCookie.value = userForCookie(result);
        }
        updateAuthHeaders();
    };

    const externalLoginDelete = async (provider) => {
        await externalLoginDeleteAction(provider);
        const result = externalLoginDeleteItem.value;
        if (result && result.accessToken) {
            accessToken.value = result.accessToken;
            _user.value = result;
            // Store in cookies
            accessTokenCookie.value = result.accessToken;
            userCookie.value = userForCookie(result);
            updateAuthHeaders();
        }

    }

    // A two-step verification handed back a token carrying the proof: use it
    // from now on, and refresh the user so mfaVerified reads true.
    const adoptProof = async (token) => {
        if (!token) return;
        accessToken.value = token;
        accessTokenCookie.value = token;
        updateAuthHeaders();
        await updateLocalUser();
    };

    // Switch the session to a signed-in user shape (e.g. the kept account after a merge).
    const adoptSession = (result) => {
        if (!result || !result.accessToken) return;
        accessToken.value = result.accessToken;
        _user.value = result;
        accessTokenCookie.value = result.accessToken;
        userCookie.value = userForCookie(result);
        updateAuthHeaders();
    };

    const externalLoginLink = async (credentials) => {
        await externalLoginLinkAction(credentials);
        const result = externalLoginLinkItem.value;
        if (result && result.accessToken) {
            accessToken.value = result.accessToken;
            _user.value = result;
            // Store in cookies
            accessTokenCookie.value = result.accessToken;
            userCookie.value = userForCookie(result);
            updateAuthHeaders();
        }

    };

    const externalLogin = async (credentials) => {
        await externalLoginAction(credentials);
        const result = externalLoginItem.value;
        if (result && result.accessToken) {
            accessToken.value = result.accessToken;
            _user.value = result;
            // Store in cookies
            accessTokenCookie.value = result.accessToken;
            userCookie.value = userForCookie(result);
        }
        updateAuthHeaders();
    };

    const joinWithInviteLink = async (inviteLinkId, authId, message, extra = {}) => {
        joinPending.value = true;
        joinError.value = null;
        try {
            const {joinInviteLink} = useInviteLink();
            const result = await joinInviteLink(inviteLinkId, authId, message, extra);
            if (result && result.accessToken) {
                accessToken.value = result.accessToken;
                _user.value = result;
                // Store in cookies
                accessTokenCookie.value = result.accessToken;
                userCookie.value = userForCookie(result);
                updateAuthHeaders();
            }
            return result;
        } catch (e) {
            joinError.value = e;
            throw e;
        } finally {
            joinPending.value = false;
        }
    };

    const validateShortLivedToken = async (token) => {
        const {httpRequest} = useHttpRequest(); // Access the HTTP request utility
        let result = null;
        try {
            // Perform the network request to validate the token
            result = await httpRequest(
                endpoints.auth.validateShortLivedToken(),
                {"token": token},
            );

            if (result && result.user) {
                const user = result.user;
                accessToken.value = user.accessToken;
                _user.value = user;
                accessTokenCookie.value = user.accessToken;
                userCookie.value = userForCookie(user);
                updateAuthHeaders();
            }
            return result;
        } catch (error) {
            console.error("Error validating short-lived token:", error);
            throw error;
        }
    };

    const impersonateUser = async (userId) => {
        await impersonate(userId + "/impersonate");
        const result = impersonateItem.value;
        impersonateLoading.value = true;
        if (result) {
            impersonatingAccessToken.value = result;
            impersonatingAccessTokenCookie.value = result;
            updateAuthHeaders();
            await updateLocalUser();
            await router.replace({path: route.path, force: true});
        }
        impersonateLoading.value = false;
    };

    const stopImpersonating = async () => {
        impersonatingAccessToken.value = null;
        impersonatingUser.value = null;
        updateAuthHeaders();
        await updateLocalUser();
        await router.replace({path: route.path, force: true});
    };
    const forgotPassword = async (data) => {
        const {token} = await executeRecaptcha("forgotPassword");
        data.RecaptchaResponse = token;
        await forgotPasswordAction(data);
    };
    const register = async (credentials) => {
        const {token} = await executeRecaptcha("register");
        credentials.RecaptchaResponse = token;
        await registerAction(credentials);
    };

    const logout = async () => {
        // Disconnect SignalR first and WAIT for it: stop() sends the hub close
        // handshake that marks the connection disconnected server-side. Fired
        // un-awaited, navigation/reload can kill it mid-flight and the server
        // keeps the user "online" until the stale-connection cleanup.
        if (process.client && signalR) {
            try {
                await signalR.disconnect();
            } catch (err) {
                console.warn('SignalR disconnect during logout failed:', err);
            }
        }

        // Stripe embedded components hold their own user session per club
        // account — Stripe asks us to invalidate it on app logout (and only then).
        if (process.client) {
            try {
                useStripeConnect().logoutAll();
            } catch (err) {
                console.warn('Stripe Connect logout failed:', err);
            }
        }

        _user.value = null;
        accessToken.value = null;
        impersonatingUser.value = null;
        impersonatingAccessToken.value = null;

        // Clear cookies
        userCookie.value = null;
        accessTokenCookie.value = null;
        impersonatingUserCookie.value = null;
        impersonatingAccessTokenCookie.value = null;
        updateAuthHeaders();

    };

    watch(_user, (newVal) => {
        if (newVal) {
            userCookie.value = userForCookie(newVal);
        } else {
            userCookie.value = null;
        }
    });

    watch(accessToken, (newVal) => {
        if (newVal) {
            accessTokenCookie.value = newVal;
        } else {
            accessTokenCookie.value = null;
        }
    });

    watch(impersonatingUser, (newVal) => {
        if (newVal) {
            impersonatingUserCookie.value = userForCookie(newVal);
        } else {
            impersonatingUserCookie.value = null;
        }
    });

    watch(impersonatingAccessToken, (newVal) => {
        if (newVal) {
            impersonatingAccessTokenCookie.value = newVal;
        } else {
            impersonatingAccessTokenCookie.value = null;
        }
    });

    const isAuthenticated = computed(() => _user.value !== null && accessToken.value !== null);

    // Manage SignalR connection based on authentication state (client-side only).
    // Started by init, never by merely using this composable, so a session free page
    // (sessionFreeRoutes) opens no live connection. A shared state keeps it to one watcher.
    const signalRWatcherInitialized = useState('signalR-watcher-initialized', () => false);

    const startLiveConnection = () => {
        if (!process.client || !signalR || signalRWatcherInitialized.value) return;
        signalRWatcherInitialized.value = true;

        watch(isAuthenticated, async (authenticated, wasAuthenticated) => {
            if (authenticated && !wasAuthenticated) {
                // User just logged in - connect SignalR
                console.log('User authenticated, connecting SignalR...');
                try {
                    await signalR.connect('/signalr/v1');
                } catch (error) {
                    console.error('Failed to connect SignalR:', error);
                }
            } else if (!authenticated && wasAuthenticated) {
                // User just logged out - disconnect SignalR
                console.log('User logged out, disconnecting SignalR...');
                signalR.disconnect();
            }
        }, { immediate: true }); // Run immediately to connect if already authenticated

        // Impersonation swaps the effective identity but the socket keeps the identity
        // it authenticated with — leaving it connected as the admin while the UI (and
        // IndexedDB scoping) is the impersonated user leaks the admin's live messages
        // into the other account's local cache. Reconnect so the hub identity, user
        // routing and conversation groups all match the effective user.
        watch(impersonatingAccessToken, async (newToken, oldToken) => {
            if (newToken === oldToken || !isAuthenticated.value) return;
            console.log('Impersonation changed, reconnecting SignalR with effective identity...');
            try {
                await signalR.disconnect();
                await signalR.connect('/signalr/v1');
            } catch (error) {
                console.error('Failed to reconnect SignalR after impersonation change:', error);
            }
        });
    };

    const isSuperUser = computed(() => isAuthenticated.value && _user.value && _user.value.superAdmin);

    // Beta: the assistant is limited to club admins (and super admins). Mirror the
    // server-side EnsureBetaAccess gate (SLL.Api). Drop this gate to open it up.
    const canUseAssistant = computed(() => isAuthenticated.value && _user.value
        && (_user.value.superAdmin || (_user.value.adminFor && _user.value.adminFor.length > 0)));

    const isAdminOfClub = (clubId) => _user.value && _user.value.adminFor && _user.value.adminFor.indexOf(clubId) !== -1;

    const isMemberOfClub = (clubId) => _user.value && _user.value.memberOf && _user.value.memberOf.indexOf(clubId) !== -1;

    const isImpersonating = computed(() => impersonatingAccessToken.value !== null);

    /// USER VERIFICATION SHIT

    const userVerificationToken = ref({});
    const userVerificationError = ref({});
    const userVerificationSaveError = ref({});
    const userVerificationLoading = ref(false);
    const hasUserVerificationErrors = computed(() => userVerificationError.value && Object.keys(userVerificationError.value).length > 0);

    const getVerificationToken = async (token) => {
        const {httpRequest} = useHttpRequest();
        userVerificationLoading.value = true;
        userVerificationError.value = {};
        try {
            let res = await httpRequest(endpoints.auth.validateToken(token), {});
            userVerificationToken.value = res;
        } catch (e) {
            userVerificationError.value = e;
        } finally {
            userVerificationLoading.value = false;
        }
    };

    const acceptVerificationToken = async (token, data) => {
        const {httpRequest} = useHttpRequest();
        userVerificationLoading.value = true;
        userVerificationSaveError.value = {};
        try {
            let res = await httpRequest(endpoints.auth.acceptInvite(token, data), data);
            accessToken.value = res.accessToken;
            _user.value = res;
            // Store in cookies
            accessTokenCookie.value = res.accessToken;
            userCookie.value = userForCookie(res);
            updateAuthHeaders();
            await updateLocalUser();

        } catch (e) {
            userVerificationSaveError.value = e;
        } finally {
            userVerificationLoading.value = false;
        }
    };

    const declineVerificationToken = async (token) => {
        const {httpRequest} = useHttpRequest();
        userVerificationLoading.value = true;
        userVerificationSaveError.value = {};
        try {
            let res = await httpRequest(endpoints.auth.rejectInvite(token), {});
            userVerificationToken.value = res;
        } catch (e) {
            userVerificationSaveError.value = e;
        } finally {
            userVerificationLoading.value = false;
        }
    };

    return {
        user,
        userRefreshed,
        updateLocalUser,
        accessToken,
        impersonatingUser,
        impersonatingAccessToken,
        getAccessToken,
        isAuthenticated,
        isSuperUser,
        canUseAssistant,
        isAdminOfClub,
        isMemberOfClub,
        externalLogin,
        externalLoginItem,
        externalLoginLoading,
        externalLoginErrors,
        hasExternalLoginErrors,
        joinWithInviteLink,
        joinPending,
        joinError,
        login,
        loginLoading,
        loginErrors,
        hasLoginErrors,
        register,
        registerLoading,
        registerErrors,
        hasRegisterErrors,
        registerItem,
        logout,
        init,
        impersonateUser,
        stopImpersonating,
        isImpersonating,
        impersonateItem,
        impersonateLoading,
        impersonateErrors,
        userVerificationToken,
        userVerificationError,
        userVerificationSaveError,
        userVerificationLoading,
        hasUserVerificationErrors,
        validateShortLivedToken,
        getVerificationToken,
        acceptVerificationToken,
        declineVerificationToken,
        forgotPasswordItem,
        forgotPassword,
        forgotPasswordLoading,
        forgotPasswordErrors,
        hasForgotPasswordErrors,
        loginVerificationItem,
        loginVerification,
        loginVerificationLoading,
        loginVerificationErrors,
        hasLoginVerificationErrors,
        registerVerificationItem,
        registerVerification,
        registerVerificationLoading,
        registerVerificationErrors,
        hasRegisterVerificationErrors,
        externalLoginLinkItem,
        externalLoginLinkAction,
        adoptSession,
        externalLoginLinkLoading,
        externalLoginLinkErrors,
        hasExternalLoginLinkErrors,
        externalLoginDeleteItem,
        externalLoginDeleteAction,
        externalLoginDeleteLoading,
        externalLoginDeleteErrors,
        hasExternalLoginDeleteErrors,
        externalLoginDelete,
        externalLoginLink,
        adoptProof,
    };
}
